// Центр помощи покупателя (fd-support-01): публичный FAQ + обращения с тредом.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api";
import type { CreateTicketRequest, SupportTicketDetail } from "@/api/types";
import { useProfile } from "@/store/profile";

export const supportKeys = {
	root: ["support"] as const,
	faq: (category: string) => [...supportKeys.root, "faq", category] as const,
	tickets: (principalId: string) => [...supportKeys.root, "tickets", principalId] as const,
	ticket: (principalId: string, id: string) => [...supportKeys.root, "ticket", principalId, id] as const,
};

// FAQ — публичный, доступен без входа.
export function useSupportFaq(category?: string) {
	return useQuery({
		queryKey: supportKeys.faq(category ?? "all"),
		queryFn: () => api.support.faq(category),
		staleTime: 5 * 60_000,
	});
}

const useAuthedPrincipal = () => {
	const token = useProfile((state) => state.token);
	const user = useProfile((state) => state.user);
	const hydrating = useProfile((state) => state.isHydrating);
	return { principalId: user?.id ?? "guest", enabled: Boolean(token && user?.id && !hydrating) };
};

export function useSupportTickets() {
	const { principalId, enabled } = useAuthedPrincipal();
	return useQuery({
		queryKey: supportKeys.tickets(principalId),
		queryFn: () => api.support.listTickets(),
		enabled,
	});
}

export function useSupportTicket(id: string | null) {
	const { principalId, enabled } = useAuthedPrincipal();
	return useQuery({
		queryKey: supportKeys.ticket(principalId, id ?? "missing"),
		queryFn: () => api.support.getTicket(id ?? ""),
		enabled: enabled && Boolean(id),
	});
}

export function useCreateTicket() {
	const { principalId } = useAuthedPrincipal();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (input: CreateTicketRequest) => api.support.createTicket(input),
		onSuccess: (ticket: SupportTicketDetail) => {
			queryClient.setQueryData(supportKeys.ticket(principalId, ticket.id), ticket);
			void queryClient.invalidateQueries({ queryKey: supportKeys.tickets(principalId) });
		},
	});
}

export function useAddTicketMessage(id: string) {
	const { principalId } = useAuthedPrincipal();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (body: string) => api.support.addMessage(id, body),
		onSuccess: (ticket: SupportTicketDetail) => {
			queryClient.setQueryData(supportKeys.ticket(principalId, id), ticket);
			void queryClient.invalidateQueries({ queryKey: supportKeys.tickets(principalId) });
		},
	});
}

export function useCloseTicket(id: string) {
	const { principalId } = useAuthedPrincipal();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: () => api.support.closeTicket(id),
		onSuccess: (ticket: SupportTicketDetail) => {
			queryClient.setQueryData(supportKeys.ticket(principalId, id), ticket);
			void queryClient.invalidateQueries({ queryKey: supportKeys.tickets(principalId) });
		},
	});
}
